/**
 * UK age + school-year-group derivation.
 *
 * Why a dedicated module?
 *   In the UK, a child's "school year" is fixed by their age on
 *   31 August of the academic year (the cut-off — children turning
 *   the relevant age on or before 31 August enter that year). We
 *   want every part of the platform — Daily Roster, Children list,
 *   Bookings, Transport manifests, Enrolment printable — to use the
 *   same canonical mapping so there are no conflicting labels.
 *
 *   Reception starts at age 4 → 5 (rising 5 by 31 Aug).
 *   Year 1   = 5 → 6
 *   …
 *   Year 13  = 17 → 18.
 *
 *   Children younger than reception age are bucketed into one of:
 *     • "Baby" (0 - 1 yrs)
 *     • "Toddler" (1 - 2 yrs)
 *     • "Pre-school" (2 - 4 yrs)
 *     • "Nursery" (the year before reception, i.e. age 3 → 4)
 *
 *   The derived `key_stage` follows the standard DfE mapping:
 *     EYFS  : Nursery + Reception
 *     KS1   : Years 1 - 2
 *     KS2   : Years 3 - 6
 *     KS3   : Years 7 - 9
 *     KS4   : Years 10 - 11
 *     KS5   : Years 12 - 13
 */

/** Parse any DOB representation (Date, ISO string, datetime string) into a Date. */
export function parseDob(input) {
  if (!input) return null;
  if (input instanceof Date) return Number.isNaN(input.getTime()) ? null : input;
  const d = new Date(input);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Years between two dates, accounting for partial years (whole years complete). */
export function ageInYears(dob, asOf = new Date()) {
  const d = parseDob(dob);
  if (!d) return null;
  let years = asOf.getFullYear() - d.getFullYear();
  const m = asOf.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && asOf.getDate() < d.getDate())) years -= 1;
  return years;
}

/** Months-and-years label for under-2s ("11 months") and under-3s ("2 yrs 3 mo"). */
export function ageLabel(dob, asOf = new Date()) {
  const d = parseDob(dob);
  if (!d) return '';
  const yrs = ageInYears(d, asOf);
  if (yrs === null) return '';
  if (yrs >= 2) return `${yrs} yrs`;
  // Months for the under-2 cohort (more meaningful to staff).
  let months = (asOf.getFullYear() - d.getFullYear()) * 12 + (asOf.getMonth() - d.getMonth());
  if (asOf.getDate() < d.getDate()) months -= 1;
  if (months < 0) months = 0;
  if (months < 12) return `${months} mo`;
  const remMonths = months - 12;
  return remMonths > 0 ? `1 yr ${remMonths} mo` : '1 yr';
}

/**
 * Return the UK academic-year start date (1 September) that applies on `asOf`.
 * For dates between 1 Jan and 31 Aug we use last September, otherwise this September.
 */
function academicYearStart(asOf = new Date()) {
  const y = asOf.getFullYear();
  const sept = new Date(y, 8, 1); // 1 Sep this year (month is 0-indexed)
  return asOf >= sept ? sept : new Date(y - 1, 8, 1);
}

/**
 * UK school-year derivation — what year would the child be in this academic year?
 * Returns: { code, label, key_stage, age_on_31_aug }
 */
export function ukYearGroup(dob, asOf = new Date()) {
  const d = parseDob(dob);
  if (!d) return null;
  const ayStart = academicYearStart(asOf);
  // Cut-off = 31 Aug of the academic year (the day before 1 Sep).
  const cutoff = new Date(ayStart.getFullYear(), 7, 31); // 31 Aug ayStart year
  const ageOnCutoff = ageInYears(d, cutoff);

  // Map age-on-31-Aug → year label/code.
  // age 4 → Reception, age 5 → Y1, etc.
  if (ageOnCutoff === null) return null;
  if (ageOnCutoff < 0) {
    return { code: 'unborn', label: '—', key_stage: '—', age_on_31_aug: ageOnCutoff };
  }
  if (ageOnCutoff < 1) return { code: 'baby', label: 'Baby', key_stage: 'EYFS', age_on_31_aug: ageOnCutoff };
  if (ageOnCutoff < 2) return { code: 'toddler', label: 'Toddler', key_stage: 'EYFS', age_on_31_aug: ageOnCutoff };
  if (ageOnCutoff < 3) return { code: 'preschool_2', label: 'Pre-school (2)', key_stage: 'EYFS', age_on_31_aug: ageOnCutoff };
  if (ageOnCutoff === 3) return { code: 'nursery', label: 'Nursery', key_stage: 'EYFS', age_on_31_aug: ageOnCutoff };
  if (ageOnCutoff === 4) return { code: 'reception', label: 'Reception', key_stage: 'EYFS', age_on_31_aug: ageOnCutoff };

  const yearNum = ageOnCutoff - 4; // age 5 → Y1, age 6 → Y2 …
  if (yearNum >= 1 && yearNum <= 13) {
    let ks;
    if (yearNum <= 2) ks = 'KS1';
    else if (yearNum <= 6) ks = 'KS2';
    else if (yearNum <= 9) ks = 'KS3';
    else if (yearNum <= 11) ks = 'KS4';
    else ks = 'KS5';
    return {
      code: `year_${yearNum}`,
      label: `Year ${yearNum}`,
      key_stage: ks,
      age_on_31_aug: ageOnCutoff,
    };
  }

  // Past compulsory schooling.
  return { code: 'post_school', label: 'Post-school', key_stage: '—', age_on_31_aug: ageOnCutoff };
}

/**
 * Pretty single-line label combining age and year group.
 * e.g. "5 yrs · Year 1 (KS1)" or "11 mo · Baby (EYFS)".
 * Used in the children list, daily roster, etc.
 */
export function ageAndYearLabel(dob, asOf = new Date()) {
  const yg = ukYearGroup(dob, asOf);
  const age = ageLabel(dob, asOf);
  if (!yg && !age) return '';
  if (!yg) return age;
  if (!age) return `${yg.label} (${yg.key_stage})`;
  return `${age} · ${yg.label} (${yg.key_stage})`;
}

/**
 * List of selectable year-group codes/labels — used by admin edit forms
 * when an admin wants to override the auto-derived value (e.g. a child
 * who's been held back / accelerated).
 */
export const YEAR_GROUP_OPTIONS = [
  { code: 'baby', label: 'Baby (0-1)' },
  { code: 'toddler', label: 'Toddler (1-2)' },
  { code: 'preschool_2', label: 'Pre-school 2yr' },
  { code: 'nursery', label: 'Nursery (3-4)' },
  { code: 'reception', label: 'Reception' },
  ...Array.from({ length: 13 }, (_, i) => ({ code: `year_${i + 1}`, label: `Year ${i + 1}` })),
];
